import React, { useState, useEffect, useRef } from 'react';

const TtsOverlay: React.FC = () => {
  const [settingsId, setSettingsId] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [isWebSocketConnected, setIsWebSocketConnected] = useState<boolean>(false);

  const websocketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioQueueRef = useRef<any[]>([]);
  const isPlayingQueueRef = useRef<boolean>(false);
  const queueDelaySeconds = 1;

  // Get settings_id and user_id from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id') || urlParams.get('settings_id');
    const user = urlParams.get('user') || urlParams.get('user_id');

    if (id && user) {
      setSettingsId(id);
      setUserId(user);
      console.log('[INIT] Settings ID:', id);
      console.log('[INIT] User ID:', user);
    } else {
      console.error('[INIT] ✗ MISSING REQUIRED PARAMETERS!');
      console.error('[INIT] Add ?id=SETTINGS_ID&user=USER_ID to the URL');
    }
  }, []);

  // Initialize audio context
  const initializeAudioContext = async () => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        console.log('[AUDIO] Audio context initialized successfully');
      } catch (error) {
        console.error('[AUDIO] Failed to initialize audio context:', error);
      }
    }

    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      console.log('[AUDIO] Resuming suspended audio context...');
      await audioContextRef.current.resume();
      console.log('[AUDIO] Audio context resumed');
    }
  };

  // Connect to WebSocket
  useEffect(() => {
    if (!settingsId || !userId) return;

    const API_URL = 'https://dork-tts-server.onrender.com';
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://') + `/ws/tts/${settingsId}/${userId}`;

    console.log('[WS] Connecting to WebSocket:', wsUrl);

    const connectWebSocket = () => {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[WS] ✓ WebSocket connected successfully');
        console.log('[WS] Connected to room for user:', settingsId + '/' + userId);
        console.log('[WS] Ready to receive TTS messages');
        setIsWebSocketConnected(true);
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('[WS] ← Message received - Type:', data.type);
        console.log('[WS] Message for user:', settingsId + '/' + userId);

        switch (data.type) {
          case 'connected':
            console.log('[WS] Proxy connected to Fish Audio');
            break;

          case 'audio':
          case 'tts_message':
            console.log('[QUEUE] Audio message received');
            console.log('[QUEUE] Text:', data.text || 'Audio message');
            console.log('[QUEUE] Audio data length:', data.audio ? data.audio.length : 0);

            if (data.audio) {
              audioQueueRef.current.push({
                text: data.text || 'Audio message',
                audio: data.audio,
                timestamp: Date.now()
              });
              console.log('[QUEUE] Added to queue - Queue length:', audioQueueRef.current.length);
              processQueue();
            }
            break;

          case 'finish':
            console.log('[WS] TTS session finished');
            break;

          case 'error':
            console.error('[WS] ✗ Error:', data.message);
            break;

          default:
            console.log('[WS] Unknown message type:', data.type, data);
        }
      };

      ws.onclose = () => {
        console.log('[WS] ✗ WebSocket disconnected');
        setIsWebSocketConnected(false);
        websocketRef.current = null;

        // Attempt to reconnect after 3 seconds
        console.log('[WS] Attempting to reconnect in 3 seconds...');
        setTimeout(() => {
          console.log('[WS] Reconnecting...');
          connectWebSocket();
        }, 3000);
      };

      ws.onerror = (error) => {
        console.error('[WS] ✗ WebSocket error:', error);
        setIsWebSocketConnected(false);
      };

      websocketRef.current = ws;
    };

    connectWebSocket();

    // Initialize audio context on first user interaction
    const handleClick = () => {
      console.log('[INIT] User interaction detected - initializing audio context');
      initializeAudioContext();
    };
    document.addEventListener('click', handleClick, { once: true });

    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      document.removeEventListener('click', handleClick);
    };
  }, [settingsId, userId]);

  // Play audio data
  const playAudioData = async (audioData: string): Promise<void> => {
    try {
      console.log('[AUDIO] Initializing audio context...');
      await initializeAudioContext();

      console.log('[AUDIO] Converting base64 to array buffer...');
      const binaryString = atob(audioData);
      const arrayBuffer = new ArrayBuffer(binaryString.length);
      const bytes = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      console.log('[AUDIO] Converted base64 to buffer - Size:', arrayBuffer.byteLength, 'bytes');

      console.log('[AUDIO] Decoding audio data...');
      const audioBuffer = await audioContextRef.current!.decodeAudioData(arrayBuffer);
      console.log('[AUDIO] Audio decoded - Duration:', audioBuffer.duration.toFixed(2), 'seconds');

      const source = audioContextRef.current!.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current!.destination);

      console.log('[AUDIO] ▶ Playing audio...');

      return new Promise((resolve) => {
        source.onended = () => {
          console.log('[AUDIO] ■ Audio playback finished');
          resolve();
        };
        source.start();
      });
    } catch (error) {
      console.error('[AUDIO] ✗ Error playing audio:', error);
    }
  };

  // Process audio queue
  const processQueue = async () => {
    if (isPlayingQueueRef.current) {
      console.log('[QUEUE] Already processing queue, skipping');
      return;
    }

    if (audioQueueRef.current.length === 0) {
      console.log('[QUEUE] Queue is empty');
      return;
    }

    console.log('[QUEUE] Starting queue processing - Items in queue:', audioQueueRef.current.length);
    isPlayingQueueRef.current = true;

    while (audioQueueRef.current.length > 0) {
      const message = audioQueueRef.current.shift()!;
      const timestamp = new Date(message.timestamp).toLocaleTimeString();

      console.log('[QUEUE] ━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('[QUEUE] Processing message from', timestamp);
      console.log('[QUEUE] Text:', message.text);
      console.log('[QUEUE] Items remaining after this:', audioQueueRef.current.length);

      if (message.audio) {
        await playAudioData(message.audio);
      } else {
        console.log('[QUEUE] ⚠ No audio data in message');
      }

      // Wait for delay between messages
      if (audioQueueRef.current.length > 0 && queueDelaySeconds > 0) {
        console.log('[QUEUE] Waiting', queueDelaySeconds, 'seconds before next message...');
        await new Promise(resolve => setTimeout(resolve, queueDelaySeconds * 1000));
      }
    }

    console.log('[QUEUE] ━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('[QUEUE] Queue processing complete');
    isPlayingQueueRef.current = false;
  };

  // Periodic status check
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('[STATUS] User:', settingsId + '/' + userId,
        '| WebSocket:', isWebSocketConnected ? '✓ Connected' : '✗ Disconnected',
        '| Queue:', audioQueueRef.current.length, 'messages',
        '| Playing:', isPlayingQueueRef.current ? 'Yes' : 'No');
    }, 30000); // Log status every 30 seconds

    return () => clearInterval(interval);
  }, [settingsId, userId, isWebSocketConnected]);

  return (
    <div style={{
      margin: 0,
      padding: 0,
      backgroundColor: 'transparent',
      fontFamily: 'monospace',
      minHeight: '100vh'
    }}>
      {/* Transparent overlay - all interaction is via console */}
    </div>
  );
};

export default TtsOverlay;
